import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import { Box, Button, Slide, Snackbar, Typography } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { serializeFlagsSnapshot } from '../utils/flagsSnapshot';

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const VERSION_URL = '/version.json';
const FLAGS_URL = '/api/feature-flags/me';

/**
 * Détecte deux événements qui nécessitent un rechargement de l'app et propose de
 * recharger — pour ne plus demander aux agences de faire CTRL+SHIFT+R :
 *
 * 1. Déploiement d'une nouvelle version du frontend (cf. vite.config.ts) : le build
 *    injecte son identifiant dans `__APP_VERSION__` (bundle courant) ET l'écrit dans
 *    `dist/version.json`. On re-fetch `version.json` SANS cache (`cache: 'no-store'`
 *    + cache-buster) et on compare : si la version en ligne diffère de celle chargée
 *    → une nouvelle version est déployée.
 *
 * 2. Changement des feature flags de l'agence (ex. un god active FormCGIA via
 *    /admin/feature-flags) : `useMyFeatureFlags` ne re-fetch jamais spontanément
 *    (staleTime 5 min, pas de refetch on focus), donc sans reload l'agence resterait
 *    sur l'ancien formulaire. On poll `/api/feature-flags/me`, on mémorise le premier
 *    snapshot, et toute différence ultérieure → même encart de rechargement.
 *
 * Les deux checks tournent au démarrage, toutes les 2 min, et au retour de focus de
 * l'onglet. Le rechargement n'est JAMAIS forcé (une agence peut être en pleine
 * saisie) : un gros bouton « Mettre à jour » déclenche `window.location.reload()`,
 * qui recharge tout le bundle et re-fetch les flags. ⚠ Pour que ce reload prenne
 * bien la nouvelle version, `index.html` doit être servi en `no-cache` côté Apache
 * — sinon l'encart reviendrait en boucle.
 */
export const AppUpdatePrompt = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Miroir de `updateAvailable` lisible dans le closure du polling : une fois
  // l'encart affiché, inutile de continuer à fetch.
  const updateFlaggedRef = useRef(false);
  // Version du bundle chargé. En dev, `__APP_VERSION__` existe mais `/version.json`
  // n'est pas servi → le fetch échoue et rien ne s'affiche (comportement voulu).
  const currentVersion = useRef<string>(typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '');
  // Snapshot des flags de l'agence au premier fetch réussi, avec l'uid propriétaire :
  // si l'utilisateur change (logout/login), le snapshot repart de zéro pour ne pas
  // comparer les flags de deux comptes différents.
  const flagsSnapshot = useRef<{ uid: string; serialized: string } | null>(null);

  const flagUpdateAvailable = () => {
    updateFlaggedRef.current = true;
    setUpdateAvailable(true);
  };

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      if (!currentVersion.current) return;
      try {
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (!cancelled && data.version && data.version !== currentVersion.current) {
          flagUpdateAvailable();
        }
      } catch {
        /* hors-ligne / 404 (dev) / JSON invalide → on ignore silencieusement */
      }
    };

    const checkFlags = async () => {
      const uid = getAuth().currentUser?.uid;
      if (!uid) return;
      try {
        const res = await makeAuthenticatedApiCall<{ success: boolean; flags?: Partial<Record<string, boolean>> }>(
          'get',
          FLAGS_URL,
        );
        if (cancelled || !res.data?.success) return;
        const serialized = serializeFlagsSnapshot(res.data.flags ?? {});
        const previous = flagsSnapshot.current;
        if (!previous || previous.uid !== uid) {
          flagsSnapshot.current = { uid, serialized };
          return;
        }
        if (previous.serialized !== serialized) {
          flagUpdateAvailable();
        }
      } catch {
        /* non connecté / hors-ligne / 401 → on ignore silencieusement */
      }
    };

    const check = async () => {
      if (updateFlaggedRef.current) return;
      await Promise.all([checkVersion(), checkFlags()]);
    };

    void check(); // check initial
    const interval = setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={Slide}
      sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          backgroundColor: '#1c1c22',
          color: '#fff',
          padding: '14px 18px',
          borderRadius: '14px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.28)',
          maxWidth: '92vw',
        }}
      >
        <AutorenewRoundedIcon sx={{ color: '#FFD400', fontSize: 28, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.25, color: '#ffffff' }}>
            Nouvelle version disponible
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.3, marginTop: '2px', color: 'rgba(255,255,255,0.72)' }}>
            Rechargez pour obtenir la dernière mise à jour de l'application.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={() => window.location.reload()}
          sx={{
            flexShrink: 0,
            fontWeight: 800,
            textTransform: 'none',
            borderRadius: '10px',
            paddingX: 2.5,
            backgroundColor: '#FFD400',
            color: '#1c1c22',
            '&:hover': { backgroundColor: '#f5cc00' },
          }}
        >
          Mettre à jour
        </Button>
      </Box>
    </Snackbar>
  );
};
